import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * Stage-3 N-1.2 — admin form for the message + attachment retention
 * windows that travel through the policy snapshot to the microservice
 * janitor. All values are positive integer days (or megabytes for the
 * attachment cap). Defaults are seeded by services/settings.js#DEFAULTS
 * on first boot; this UI only surfaces the values back for editing.
 */

const RETENTION_FIELDS = Object.freeze([
  // Chat channels — general traffic profile.
  {
    section: 'Chat channels',
    icon: 'fa-comments',
    items: [
      {
        key: 'retention_chat_messages_days',
        label: 'Messages, days',
        unit: 'days',
        help: 'Server-side retention for text messages in chat channels.',
        max: 5 * 365,
      },
      {
        key: 'retention_chat_attachment_max_mb',
        label: 'Attachments — max size, MB',
        unit: 'MB',
        help: 'Per-file upload cap.',
        max: 1024,
      },
      {
        key: 'retention_chat_attachments_days',
        label: 'Attachments, days',
        unit: 'days',
        help: 'Server-side retention for files / images / voice notes.',
        max: 5 * 365,
      },
    ],
  },
  // Data channels — per-dataType because each carries a distinct profile.
  {
    section: 'Data channels',
    icon: 'fa-database',
    items: [
      { key: 'retention_data_cop_layers_days',     label: 'COP Layers, days',      unit: 'days', help: 'Common Operational Picture layers.', max: 5 * 365 },
      { key: 'retention_data_awds_layers_days',    label: 'AWDS Layers, days',     unit: 'days', help: 'Live sensor feed — short default.', max: 5 * 365 },
      { key: 'retention_data_cot_layers_days',     label: 'CoT Layers, days',      unit: 'days', help: 'Cursor on Target positions.', max: 5 * 365 },
      { key: 'retention_data_nffi_layers_days',    label: 'NFFI Layers, days',     unit: 'days', help: 'NATO Friendly Force Information.', max: 5 * 365 },
      { key: 'retention_data_adatp3_layers_days',  label: 'ADatP-3 Layers, days',  unit: 'days', help: 'Situational ADatP-3 messages (NBC-1, SITREP, MEDEVAC).', max: 5 * 365 },
      { key: 'retention_data_adatp3_orders_days',  label: 'ADatP-3 Orders, days',  unit: 'days', help: 'Orders inbox (FRAGO, OPORD, WARNORD). Janitors apply whichever of Layers/Orders is shorter.', max: 5 * 365 },
    ],
  },
]);

const ALL_KEYS = RETENTION_FIELDS.flatMap((s) => s.items.map((it) => it.key));

export function RetentionTab({ onSettingsChanged }) {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.settings.system()
      .then((s) => {
        if (cancelled) return;
        setSettings(s.settings);
        const d = {};
        for (const k of ALL_KEYS) d[k] = s.settings[k] ?? '';
        setDraft(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.payload?.error || err.message : 'Failed to load settings.');
        }
      });
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return ALL_KEYS.some((k) => String(draft[k]) !== String(settings[k] ?? ''));
  }, [settings, draft]);

  const errors = useMemo(() => {
    const e = {};
    for (const section of RETENTION_FIELDS) {
      for (const it of section.items) {
        const v = Number(draft[it.key]);
        if (!Number.isFinite(v) || !Number.isInteger(v) || v <= 0) {
          e[it.key] = 'Must be a positive integer.';
        } else if (it.max != null && v > it.max) {
          e[it.key] = `Maximum ${it.max}.`;
        }
      }
    }
    return e;
  }, [draft]);

  const canSave = dirty && Object.keys(errors).length === 0 && !busy;

  async function handleSave() {
    if (!canSave) return;
    setBusy(true);
    try {
      const patch = {};
      for (const k of ALL_KEYS) patch[k] = String(parseInt(draft[k], 10));
      const res = await api.settings.updateSystem(patch);
      setSettings(res.settings);
      onSettingsChanged?.();
      toast.success('Retention settings saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  function handleReset() {
    if (!settings) return;
    const d = {};
    for (const k of ALL_KEYS) d[k] = settings[k] ?? '';
    setDraft(d);
  }

  if (loadError) {
    return (
      <div role="alert" className="card border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
        {loadError}
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="card px-6 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {RETENTION_FIELDS.map((section) => (
        <div className="card" key={section.section}>
          <header className="flex items-center justify-between border-b border-line px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <i className={`fa-solid ${section.icon}`} aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-ink">{section.section}</h2>
                <p className="text-sm text-mist">Retention windows and limits enforced node-side.</p>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
            {section.items.map((it) => (
              <div key={it.key}>
                <label htmlFor={it.key} className="field-label">{it.label}</label>
                <input
                  id={it.key}
                  type="number"
                  className="field"
                  min={1}
                  max={it.max || undefined}
                  step={1}
                  value={draft[it.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [it.key]: e.target.value }))}
                  disabled={busy}
                />
                {errors[it.key] && <p className="mt-1 text-xs text-rose-600">{errors[it.key]}</p>}
                <p className="mt-1 text-xs text-mist">{it.help}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={handleReset} disabled={!dirty || busy}>
          Reset
        </button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={!canSave}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
